import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  Avatar,
  Paper
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import StarIcon from '@mui/icons-material/Star';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CategoryIcon from '@mui/icons-material/Category';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import VerifiedIcon from '@mui/icons-material/Verified';
import CelebrationIcon from '@mui/icons-material/Celebration';

const About = () => {
  const stats = [
    { icon: <GroupsIcon sx={{ fontSize: 40 }} />, number: '50,000+', label: 'Happy Customers' },
    { icon: <StarIcon sx={{ fontSize: 40 }} />, number: '15+', label: 'Years Experience' },
    { icon: <SecurityIcon sx={{ fontSize: 40 }} />, number: '100%', label: 'Safe Travel' },
    { icon: <SupportAgentIcon sx={{ fontSize: 40 }} />, number: '24/7', label: 'Support' }
  ];

  const team = [
    { name: 'Rajesh Kumar', role: 'CEO & Founder', avatar: 'RK' },
    { name: 'Priya Sharma', role: 'Head of Operations', avatar: 'PS' },
    { name: 'Amit Patel', role: 'Travel Expert', avatar: 'AP' },
    { name: 'Sneha Gupta', role: 'Customer Relations', avatar: 'SG' }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url("https://images.unsplash.com/photo-1488085061387-422e29b40080")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white'
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            About Us
          </Typography>
          <Typography variant="h6">
            Making your travel dreams come true since 2009
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Mission Section */}
        <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
              Our Mission
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              We believe that travel is not just about visiting new places - it's about creating 
              unforgettable memories and experiencing the world in its full glory. Our mission is to make travel 
              accessible, affordable, and extraordinary for everyone.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              With over 15 years of experience in the travel industry, we have helped more than 50,000 travelers 
              explore destinations across India and around the world. Our team of expert travel consultants works 
              tirelessly to craft personalized itineraries that match your dreams and budget.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1502920514313-52581002a659"
              alt="Travel Mission"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 4,
                boxShadow: 3
              }}
            />
          </Grid>
        </Grid>

        {/* Stats Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card elevation={3} sx={{ textAlign: 'center', py: 3 }}>
                <Box sx={{ color: 'primary.main', mb: 1 }}>
                  {stat.icon}
                </Box>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {stat.number}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Why Choose Us */}
        <Typography variant="h4" align="center" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
          Why Choose Us
        </Typography>
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {[
            {
              title: 'Expert Guidance',
              description: 'Our travel experts have first-hand knowledge of destinations and can provide insider tips.',
              icon: <FlightTakeoffIcon sx={{ fontSize: 40 }} />
            },
            {
              title: 'Best Prices',
              description: 'We negotiate the best deals with hotels, airlines, and local operators to give you competitive prices.',
              icon: <MonetizationOnIcon sx={{ fontSize: 40 }} />
            },
            {
              title: 'Customized Packages',
              description: 'Every traveler is unique, and so are our packages. We tailor each itinerary to your preferences.',
              icon: <CategoryIcon sx={{ fontSize: 40 }} />
            },
            {
              title: '24/7 Support',
              description: 'Our customer support team is available round the clock to assist you during your journey.',
              icon: <HeadsetMicIcon sx={{ fontSize: 40 }} />
            },
            {
              title: 'Safe & Secure',
              description: 'We partner with verified and trusted service providers to ensure your safety and security.',
              icon: <VerifiedIcon sx={{ fontSize: 40 }} />
            },
            {
              title: 'Hassle-Free Booking',
              description: 'From flights to hotels to local transport, we handle everything so you can focus on enjoying your trip.',
              icon: <CelebrationIcon sx={{ fontSize: 40 }} />
            }
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper elevation={3} sx={{ p: 4, height: '100%' }}>
                <Box sx={{ color: 'primary.main', mb: 2 }}>
                  {item.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Team Section */}
        <Typography variant="h4" align="center" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
          Our Team
        </Typography>
        <Grid container spacing={4}>
          {team.map((member, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card elevation={3} sx={{ textAlign: 'center', py: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    margin: '0 auto 16px',
                    bgcolor: 'primary.main',
                    fontSize: '2rem'
                  }}
                >
                  {member.avatar}
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  {member.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {member.role}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default About;